import { StreamableHTTPServerTransport } from '@modelcontextprotocol/sdk/server/streamableHttp.js';
import {
  isInitializeRequest,
  LoggingLevel,
  SetLevelRequestSchema,
} from '@modelcontextprotocol/sdk/types.js';
import cookieParser from 'cookie-parser';
import cors from 'cors';
import express, { Request, RequestHandler, Response } from 'express';
import fs, { existsSync } from 'fs';
import http from 'http';
import https from 'https';

import { Config } from '../config.js';
import { log } from '../logging/logger.js';
import { setNotificationLevel } from '../logging/notification.js';
import { Server } from '../server.js';
import { WebMcpServer } from '../server.web.js';
import { createSession, getSession, Session } from '../sessions.js';
import { latencyMiddleware } from './latencyMiddleware.js';
import { handlePingRequest } from './middleware.js';
import { getTableauAuthInfo } from './oauth/getTableauAuthInfo.js';
import { EmbeddedOAuthProvider, TableauOAuthProvider } from './oauth/provider.js';
import { TableauAuthInfo } from './oauth/schemas.js';
import { AuthenticatedRequest } from './oauth/types.js';
import { passthroughAuthMiddleware, X_TABLEAU_AUTH_HEADER } from './passthroughAuthMiddleware.js';
import { X_TABLEAU_MCP_CONFIG_HEADER } from './requestUtils.js';

const SESSION_ID_HEADER = 'mcp-session-id';

export async function startExpressServer({
  basePath,
  config,
  logLevel,
}: {
  basePath: string;
  config: Config;
  logLevel: LoggingLevel;
}): Promise<{ url: string; app: express.Application; server: http.Server }> {
  const app = express();

  app.use(express.json());
  app.use(express.urlencoded());
  if (config.enablePassthroughAuth) {
    // cookie-parser is used to parse the workgroup_session_id cookie for passthrough auth
    app.use(cookieParser());
  }

  app.use(
    cors({
      origin: config.corsOriginConfig,
      credentials: true,
      allowedHeaders: [
        'Content-Type',
        'Authorization',
        'Cache-Control',
        'Accept',
        'MCP-Protocol-Version',
        X_TABLEAU_AUTH_HEADER,
        X_TABLEAU_MCP_CONFIG_HEADER,
      ],
      exposedHeaders: [SESSION_ID_HEADER, 'x-session-id'],
    }),
  );

  const middleware: Array<RequestHandler> = [handlePingRequest];
  if (config.enablePassthroughAuth) {
    middleware.push(passthroughAuthMiddleware());
  }

  if (config.oauth.enabled) {
    const oauthProvider = config.oauth.embeddedAuthzServer
      ? new EmbeddedOAuthProvider()
      : new TableauOAuthProvider();

    oauthProvider.setupRoutes(app);
    middleware.push(oauthProvider.authMiddleware);
  }
  middleware.push(latencyMiddleware());

  const path = `/${basePath}`;
  app.post(path, ...middleware, createMcpServer);
  app.get(
    path,
    ...middleware,
    config.disableSessionManagement ? methodNotAllowed : handleSessionRequest,
  );
  app.delete(
    path,
    ...middleware,
    config.disableSessionManagement ? methodNotAllowed : handleSessionRequest,
  );

  const useSsl = !!(config.sslKey && config.sslCert);
  if (!useSsl) {
    return new Promise((resolve) => {
      const server = http
        .createServer(app)
        .listen(config.httpPort, () =>
          resolve({ url: `http://localhost:${config.httpPort}/${basePath}`, app, server }),
        );
    });
  }

  if (!existsSync(config.sslKey)) {
    throw new Error('SSL key file does not exist');
  }

  if (!existsSync(config.sslCert)) {
    throw new Error('SSL cert file does not exist');
  }

  const options = {
    key: fs.readFileSync(config.sslKey),
    cert: fs.readFileSync(config.sslCert),
  };

  return new Promise((resolve) => {
    const server = https
      .createServer(options, app)
      .listen(config.httpPort, () =>
        resolve({ url: `https://localhost:${config.httpPort}/${basePath}`, app, server }),
      );
  });

  async function createMcpServer(req: AuthenticatedRequest, res: Response): Promise<void> {
    try {
      let transport: StreamableHTTPServerTransport;

      if (config.disableSessionManagement) {
        const capabilities = isInitializeRequest(req.body)
          ? req.body.params.capabilities
          : undefined;
        const clientId = req.auth?.clientId;
        const server = new WebMcpServer({ capabilities, clientId });
        transport = new StreamableHTTPServerTransport({
          sessionIdGenerator: undefined,
        });

        res.on('close', () => {
          transport.close();
          server.mcpServer.close();
        });

        await connect(server, transport, logLevel, getTableauAuthInfo(req.auth));
      } else {
        const sessionId = req.headers[SESSION_ID_HEADER] as string | undefined;

        let session: Session | undefined;
        if (sessionId && (session = getSession(sessionId))) {
          transport = session.transport;
        } else if (!sessionId && isInitializeRequest(req.body)) {
          const clientInfo = req.body.params.clientInfo;
          const capabilities = req.body.params.capabilities;
          const clientId = req.auth?.clientId;
          transport = createSession({ clientInfo, capabilities, clientId });

          const server = new WebMcpServer({ clientInfo, capabilities, clientId });
          await connect(server, transport, logLevel, getTableauAuthInfo(req.auth));
        } else {
          log({
            message: 'Rejected request: no valid session ID and not an initialize request',
            level: 'error',
            logger: 'server',
          });
          res.status(400).json({
            jsonrpc: '2.0',
            error: {
              code: -32000,
              message: 'Bad Request: No valid session ID provided',
            },
            id: null,
          });
          return;
        }
      }

      await transport.handleRequest(req, res, req.body);
    } catch (error) {
      log({
        message: 'Error handling MCP request',
        level: 'error',
        logger: 'server',
        data: error,
      });
      if (!res.headersSent) {
        res.status(500).json({
          jsonrpc: '2.0',
          error: {
            code: -32603,
            message: 'Internal server error',
          },
          id: null,
        });
      }
    }
  }
}

async function connect(
  server: Server,
  transport: StreamableHTTPServerTransport,
  logLevel: LoggingLevel,
  authInfo: TableauAuthInfo | undefined,
): Promise<void> {
  await server.registerTools(authInfo);
  server.mcpServer.server.setRequestHandler(SetLevelRequestSchema, async (request) => {
    setNotificationLevel(server.mcpServer, request.params.level);
    return {};
  });

  await server.mcpServer.connect(transport);
  setNotificationLevel(server.mcpServer, logLevel);
  log({ message: 'MCP server connected to transport', level: 'debug', logger: 'server' });
}

async function methodNotAllowed(_req: Request, res: Response): Promise<void> {
  res.writeHead(405).end(
    JSON.stringify({
      jsonrpc: '2.0',
      error: {
        code: -32000,
        message: 'Method not allowed.',
      },
      id: null,
    }),
  );
}

async function handleSessionRequest(req: express.Request, res: express.Response): Promise<void> {
  const sessionId = req.headers[SESSION_ID_HEADER] as string | undefined;

  let session: Session | undefined;
  if (!sessionId || !(session = getSession(sessionId))) {
    res.status(400).send('Invalid or missing session ID');
    return;
  }

  await session.transport.handleRequest(req, res);
}
